import React, { createContext, useState, useContext, useCallback, useEffect } from "react";
import { AuthContext } from "./AuthContext";
import { GarmentMeasurementResult } from "../utilities/types/free-trial/garment-measurement-service/types";
import { convertToBase64 } from "../utilities/helper/convertToBase64";
import { submitGarmentMeasurement, submitSingleGarmentMeasurement } from "../utilities/api/garmentMeasurementService";
import { validateImage } from "../utilities/helper/validateImageUpload";

export type MeasurementUnit = "cm" | "inch";

const CM_TO_INCH = 0.393701;

interface GarmentMeasurementServiceContextValue {
  uploadedImages: string[];
  currentStep: number;
  canProceedToNextStep: boolean;
  garmentMeasurementResult: GarmentMeasurementResult | null;
  allMeasurements: (GarmentMeasurementResult | null)[];
  error: string | null;
  imageErrors: string[];
  measurementErrors: { [key: number]: string | null };
  loading: boolean;
  processingStatus: { [key: number]: "pending" | "processing" | "success" | "error" };
  maxImagesCount: number;
  defaultImagesCount: number;
  currentImageIndex: number;
  measurementUnit: MeasurementUnit;
  convertedMeasurements: GarmentMeasurementResult | null;
  showTourGuideModal: boolean;
  setCurrentImageIndex: (index: number) => void;
  setError: (error: string | null) => void;
  handlePageChange: (page: number) => void;
  handleImageUpload: (image: File | undefined, index: number) => void;
  handleMultipleImagesUpload: (images: FileList) => void;
  setCanProceedToNextStep: (value: boolean) => void;
  submitMeasurement: () => Promise<void>;
  resetGarmentMeasurementService: () => void;
  removeImage: (index: number) => void;
  addNewImageSlot: () => void;
  updateMeasurementForCurrentImage: () => void;
  toggleMeasurementUnit: () => void;
  getCurrentMeasurementError: () => string | null;
  closeTourGuideModal: () => void;
  openTourGuideModal: () => void;
}

export const GarmentMeasurementServiceContext = createContext<GarmentMeasurementServiceContextValue | null>(null);

export const GarmentMeasurementServiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const MAX_IMAGES_COUNT = 50;
  const DEFAULT_IMAGES_COUNT = 1;
  const [uploadedImages, setUploadedImages] = useState<string[]>(Array(DEFAULT_IMAGES_COUNT).fill(""));
  const [currentStep, setCurrentStep] = useState(0);
  const [canProceedToNextStep, setCanProceedToNextStep] = useState(false);
  const [garmentMeasurementResult, setGarmentMeasurementResult] = useState<GarmentMeasurementResult | null>(null);
  const [allMeasurements, setAllMeasurements] = useState<(GarmentMeasurementResult | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<string[]>(Array(DEFAULT_IMAGES_COUNT).fill(""));
  const [measurementErrors, setMeasurementErrors] = useState<{ [key: number]: string | null }>({});
  const [loading, setLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<{ [key: number]: "pending" | "processing" | "success" | "error" }>({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>("cm");
  const [showTourGuideModal, setShowTourGuideModal] = useState(true);
  const { accessToken } = useContext(AuthContext);
  const closeTourGuideModal = useCallback(() => {
    setShowTourGuideModal(false);
  }, []);
  const openTourGuideModal = useCallback(() => {
    setShowTourGuideModal(true);
  }, []);

  const convertedMeasurements = useCallback(() => {
    if (!garmentMeasurementResult) return null;

    if (measurementUnit === "cm") {
      return garmentMeasurementResult;
    }

    const converted: GarmentMeasurementResult = {
      Details: {},
      MetaData: {
        Date: garmentMeasurementResult.MetaData?.Date || new Date().toISOString().split("T")[0],
        FileNames: garmentMeasurementResult.MetaData?.FileNames || [],
      },
    };

    for (const key in garmentMeasurementResult) {
      if (key !== "Details" && key !== "MetaData" && typeof garmentMeasurementResult[key] === "number") {
        converted[key] = +(garmentMeasurementResult[key] * CM_TO_INCH).toFixed(2);
      }
    }

    for (const key in garmentMeasurementResult.Details) {
      const detail = garmentMeasurementResult.Details[key];
      converted.Details[key] = {
        ...detail,
        Size: +(detail.Size * CM_TO_INCH).toFixed(2),
      };
    }

    return converted;
  }, [garmentMeasurementResult, measurementUnit]);

  const toggleMeasurementUnit = useCallback(() => {
    setMeasurementUnit((prevUnit) => (prevUnit === "cm" ? "inch" : "cm"));
  }, []);

  const handlePageChange = (page: number) => {
    setError(null);
    setCurrentStep(page);
    setCanProceedToNextStep(false);
  };

  const validateAndUpdateProceedStatus = useCallback(() => {
    const hasValidImages = uploadedImages.some((img) => img !== "");
    const hasErrors = imageErrors.some((error) => error !== "");

    setCanProceedToNextStep(hasValidImages && !hasErrors);
  }, [uploadedImages, imageErrors]);

  const handleImageUpload = useCallback(
    async (image: File | undefined, index: number) => {
      if (image) {
        const newImageErrors = [...imageErrors];
        newImageErrors[index] = "";
        setImageErrors(newImageErrors);

        try {
          const base64 = await convertToBase64(image);
          const newUploadedImages = [...uploadedImages];
          newUploadedImages[index] = `TSHIRT_${index + 1}:${base64}`;
          setUploadedImages(newUploadedImages);

          const validation = validateImage(image);
          if (!validation.isValid) {
            newImageErrors[index] = validation.error || "Invalid image";
            setImageErrors(newImageErrors);
          }

          validateAndUpdateProceedStatus();
        } catch (error) {
          console.error("Error converting image to base64:", error);
          newImageErrors[index] = "Failed to process image";
          setImageErrors(newImageErrors);
        }
      } else {
        const newUploadedImages = [...uploadedImages];
        newUploadedImages[index] = "";
        setUploadedImages(newUploadedImages);

        const newImageErrors = [...imageErrors];
        newImageErrors[index] = "";
        setImageErrors(newImageErrors);

        validateAndUpdateProceedStatus();
      }
    },
    [imageErrors, uploadedImages, validateAndUpdateProceedStatus]
  );

  const handleMultipleImagesUpload = useCallback(
    async (images: FileList) => {
      if (!images || images.length === 0) return;

      const currentValidSlots = uploadedImages.filter((img) => img !== "").length;
      const requiredNewSlots = Math.min(images.length, MAX_IMAGES_COUNT - currentValidSlots);

      if (requiredNewSlots <= 0) {
        setError(`Maximum of ${MAX_IMAGES_COUNT} images allowed`);
        return;
      }

      let newUploadedImages = [...uploadedImages];
      let newImageErrors = [...imageErrors];

      let emptySlotIndices = newUploadedImages.map((img, index) => (img === "" ? index : -1)).filter((index) => index !== -1);

      if (emptySlotIndices.length < requiredNewSlots) {
        const slotsToAdd = requiredNewSlots - emptySlotIndices.length;
        for (let i = 0; i < slotsToAdd; i++) {
          if (newUploadedImages.length < MAX_IMAGES_COUNT) {
            newUploadedImages.push("");
            newImageErrors.push("");
            emptySlotIndices.push(newUploadedImages.length - 1);
          }
        }
      }

      setLoading(true);
      setError(null);

      try {
        const filePromises = Array.from(images)
          .slice(0, requiredNewSlots)
          .map(async (file, index) => {
            const slotIndex = emptySlotIndices[index];

            if (slotIndex === undefined) return null;

            try {
              const base64 = await convertToBase64(file);
              const validation = validateImage(file);

              return {
                slotIndex,
                base64,
                isValid: validation.isValid,
                error: validation.isValid ? "" : validation.error || "Invalid image",
              };
            } catch (error) {
              console.error("Error processing image:", error);
              return {
                slotIndex,
                base64: "",
                isValid: false,
                error: "Failed to process image",
              };
            }
          });

        const results = await Promise.all(filePromises);

        let uploadSuccessCount = 0;

        results.forEach((result) => {
          if (result && result.slotIndex !== undefined) {
            if (result.base64) {
              newUploadedImages[result.slotIndex] = `TSHIRT_${result.slotIndex + 1}:${result.base64}`;
              uploadSuccessCount++;
            }

            newImageErrors[result.slotIndex] = result.error;
          }
        });

        setUploadedImages(newUploadedImages);
        setImageErrors(newImageErrors);

        if (uploadSuccessCount > 0) {
        } else {
          setError("Failed to upload any images");
        }
      } catch (error) {
        console.error("Error in multiple upload:", error);
        setError("An unexpected error occurred during upload");
      } finally {
        setLoading(false);
        validateAndUpdateProceedStatus();
      }
    },
    [uploadedImages, imageErrors, MAX_IMAGES_COUNT, validateAndUpdateProceedStatus]
  );

  const removeImage = useCallback(
    (index: number) => {
      if (uploadedImages.length <= 1) {
        return;
      }

      const newUploadedImages = [...uploadedImages];
      newUploadedImages.splice(index, 1);
      setUploadedImages(newUploadedImages);

      const newImageErrors = [...imageErrors];
      newImageErrors.splice(index, 1);
      setImageErrors(newImageErrors);

      const newAllMeasurements = [...allMeasurements];
      if (newAllMeasurements.length > index) {
        newAllMeasurements.splice(index, 1);
        setAllMeasurements(newAllMeasurements);
      }

      const newProcessingStatus = { ...processingStatus };
      delete newProcessingStatus[index];

      const updatedStatus: typeof processingStatus = {};
      Object.entries(newProcessingStatus).forEach(([key, value]) => {
        const numKey = parseInt(key);
        if (numKey > index) {
          updatedStatus[numKey - 1] = value;
        } else {
          updatedStatus[numKey] = value;
        }
      });
      setProcessingStatus(updatedStatus);

      const newMeasurementErrors = { ...measurementErrors };
      delete newMeasurementErrors[index];

      const updatedMeasurementErrors: typeof measurementErrors = {};
      Object.entries(newMeasurementErrors).forEach(([key, value]) => {
        const numKey = parseInt(key);
        if (numKey > index) {
          updatedMeasurementErrors[numKey - 1] = value;
        } else {
          updatedMeasurementErrors[numKey] = value;
        }
      });
      setMeasurementErrors(updatedMeasurementErrors);

      validateAndUpdateProceedStatus();
    },
    [uploadedImages, imageErrors, allMeasurements, processingStatus, measurementErrors, validateAndUpdateProceedStatus]
  );

  const addNewImageSlot = useCallback(() => {
    if (uploadedImages.length < MAX_IMAGES_COUNT) {
      setUploadedImages([...uploadedImages, ""]);
      setImageErrors([...imageErrors, ""]);
    }
  }, [uploadedImages, imageErrors, MAX_IMAGES_COUNT]);

  const processImage = useCallback(
    async (image: string, index: number) => {
      if (!accessToken) return null;

      const newProcessingStatus = { ...processingStatus };
      newProcessingStatus[index] = "processing";
      setProcessingStatus(newProcessingStatus);

      try {
        const response = await submitSingleGarmentMeasurement(accessToken, image);

        if (!response.ok) {
          setProcessingStatus((prev) => ({ ...prev, [index]: "error" }));
          setMeasurementErrors((prev) => ({ ...prev, [index]: response.message }));
          console.error(`Error processing image ${index}:`, response.message);
          return null;
        }

        const resultFromResponse = response.data.result || {
          Details: {},
          MetaData: { Date: new Date().toISOString().split("T")[0], FileNames: [] },
        };

        const hasMeasurements = Object.keys(resultFromResponse).some((key) => key !== "Details" && key !== "MetaData" && typeof resultFromResponse[key] === "number" && resultFromResponse[key] > 0);

        if (hasMeasurements) {
          setProcessingStatus((prev) => ({ ...prev, [index]: "success" }));
          return resultFromResponse;
        } else {
          setProcessingStatus((prev) => ({ ...prev, [index]: "success" }));
          setMeasurementErrors((prev) => ({ ...prev, [index]: "No measurements detected for this image" }));
          return resultFromResponse;
        }
      } catch (error) {
        setProcessingStatus((prev) => ({ ...prev, [index]: "error" }));
        const errorMessage = error instanceof Error ? error.message : "Failed to process image";
        setMeasurementErrors((prev) => ({ ...prev, [index]: errorMessage }));
        console.error(`Error processing image ${index}:`, error);
        return null;
      }
    },
    [accessToken, processingStatus]
  );

  const submitMeasurement = async () => {
    if (imageErrors.some((error) => error !== "")) {
      setError("Please fix image errors before proceeding");
      return;
    }

    if (!uploadedImages.some((img) => img !== "") || !accessToken) {
      setError("Please upload at least one image");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setMeasurementErrors({});

      const filteredImages = uploadedImages.filter((img) => img !== "");
      const newAllMeasurements: (GarmentMeasurementResult | null)[] = new Array(filteredImages.length).fill(null);

      const initialProcessingStatus: typeof processingStatus = {};
      filteredImages.forEach((_, idx) => {
        initialProcessingStatus[idx] = "pending";
      });
      setProcessingStatus(initialProcessingStatus);

      setCurrentStep(1);

      const emptyResult: GarmentMeasurementResult = {
        Details: {},
        MetaData: { Date: new Date().toISOString().split("T")[0], FileNames: [] },
      };

      const processedResults = [];
      let atLeastOneSuccessful = false;

      for (let i = 0; i < filteredImages.length; i++) {
        const currentImage = filteredImages[i];

        setProcessingStatus((prev) => ({ ...prev, [i]: "processing" }));

        try {
          const response = i === 0 ? await submitGarmentMeasurement(accessToken, { garment_images: [currentImage] }) : await submitSingleGarmentMeasurement(accessToken, currentImage);

          if (!response.ok) {
            setProcessingStatus((prev) => ({ ...prev, [i]: "error" }));
            setMeasurementErrors((prev) => ({ ...prev, [i]: response.message }));
            console.error(`Error processing image ${i}:`, response.message);
            processedResults.push(null);
          } else {
            const resultFromResponse = response.data.result || emptyResult;

            const hasMeasurements = Object.keys(resultFromResponse).some(
              (key) => key !== "Details" && key !== "MetaData" && typeof resultFromResponse[key] === "number" && resultFromResponse[key] > 0
            );

            if (hasMeasurements) {
              processedResults.push(resultFromResponse);
              setProcessingStatus((prev) => ({ ...prev, [i]: "success" }));
              atLeastOneSuccessful = true;
            } else {
              processedResults.push(resultFromResponse);
              setProcessingStatus((prev) => ({ ...prev, [i]: "success" }));
              setMeasurementErrors((prev) => ({ ...prev, [i]: "No measurements detected for this image" }));
            }

            if (i === 0) {
              setGarmentMeasurementResult(resultFromResponse);
            }
          }
        } catch (error) {
          console.error(`Error processing image ${i}:`, error);
          setProcessingStatus((prev) => ({ ...prev, [i]: "error" }));
          const errorMessage = error instanceof Error ? error.message : "Failed to process image";
          setMeasurementErrors((prev) => ({ ...prev, [i]: errorMessage }));
          processedResults.push(null);
        }
      }

      setAllMeasurements(processedResults);

      if (!atLeastOneSuccessful && processedResults.length > 0) {
        setGarmentMeasurementResult(processedResults[0] || emptyResult);
      }

      setCanProceedToNextStep(true);
      setCurrentStep(2);
    } catch (error) {
      console.error("Error submitting garment measurement:", error);
      setError(error instanceof Error ? error.message : "An error occurred during measurement. Please try again.");
      setCurrentStep(0);
    } finally {
      setLoading(false);
    }
  };

  const updateMeasurementForCurrentImage = useCallback(() => {
    if (currentImageIndex >= 0) {
      const validImages = uploadedImages.filter((img) => img !== "");

      if (validImages.length > 0 && currentImageIndex < validImages.length) {
        if (allMeasurements && allMeasurements.length > currentImageIndex && allMeasurements[currentImageIndex]) {
          setGarmentMeasurementResult(allMeasurements[currentImageIndex]);
        } else {
          setGarmentMeasurementResult(null);
        }
      }
    }
  }, [currentImageIndex, uploadedImages, allMeasurements]);

  const getCurrentMeasurementError = useCallback(() => {
    if (currentImageIndex in measurementErrors) {
      return measurementErrors[currentImageIndex];
    }
    return null;
  }, [currentImageIndex, measurementErrors]);

  useEffect(() => {
    if (currentStep === 2 && garmentMeasurementResult) {
      updateMeasurementForCurrentImage();
    }
  }, [currentImageIndex, currentStep, garmentMeasurementResult, updateMeasurementForCurrentImage]);

  const resetGarmentMeasurementService = () => {
    setUploadedImages(Array(DEFAULT_IMAGES_COUNT).fill(""));
    setCurrentStep(0);
    setCanProceedToNextStep(false);
    setGarmentMeasurementResult(null);
    setAllMeasurements([]);
    setError(null);
    setImageErrors(Array(DEFAULT_IMAGES_COUNT).fill(""));
    setMeasurementErrors({});
    setCurrentImageIndex(0);
    setProcessingStatus({});
  };

  useEffect(() => {
    validateAndUpdateProceedStatus();
  }, [validateAndUpdateProceedStatus]);

  return (
    <GarmentMeasurementServiceContext.Provider
      value={{
        uploadedImages,
        currentStep,
        canProceedToNextStep,
        garmentMeasurementResult,
        allMeasurements,
        error,
        imageErrors,
        measurementErrors,
        loading,
        processingStatus,
        maxImagesCount: MAX_IMAGES_COUNT,
        defaultImagesCount: DEFAULT_IMAGES_COUNT,
        currentImageIndex,
        measurementUnit,
        convertedMeasurements: convertedMeasurements(),
        showTourGuideModal,
        setCurrentImageIndex,
        setError,
        handlePageChange,
        handleImageUpload,
        handleMultipleImagesUpload,
        setCanProceedToNextStep,
        submitMeasurement,
        resetGarmentMeasurementService,
        removeImage,
        addNewImageSlot,
        updateMeasurementForCurrentImage,
        toggleMeasurementUnit,
        getCurrentMeasurementError,
        closeTourGuideModal,
        openTourGuideModal,
      }}
    >
      {children}
    </GarmentMeasurementServiceContext.Provider>
  );
};

export const useGarmentMeasurementServiceContext = () => {
  const context = useContext(GarmentMeasurementServiceContext);
  if (!context) {
    throw new Error("useGarmentMeasurementServiceContext must be used within a GarmentMeasurementServiceProvider");
  }
  return context;
};
